import { useState, useEffect } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Smartphone,
  CheckCheck,
  Camera,
  FileDown,
  Sparkles,
} from 'lucide-react';

const ONBOARDING_KEY = 'car-pdi-onboarding-seen';

const OnboardingTour = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      icon: Smartphone,
      title: 'Swipe to Mark Items',
      description: 'Swipe right on any checklist item to quickly mark it as Pass, or swipe left for Fail. It\'s the fastest way to go through your inspection!',
      color: 'from-brand-500 to-teal-500',
      bgColor: 'bg-brand-50',
    },
    {
      icon: CheckCheck,
      title: 'Mark All as Pass',
      description: 'If everything in a section looks good, use the "Mark All as Pass" button to quickly complete the entire section at once.',
      color: 'from-violet-500 to-purple-500',
      bgColor: 'bg-violet-50',
    },
    {
      icon: Camera,
      title: 'Add Photo Evidence',
      description: 'Tap any item to add notes and photos. Document issues with pictures - they\'re saved locally and included in your PDF report.',
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
    },
    {
      icon: FileDown,
      title: 'Export & Share',
      description: 'When done, export your inspection as a PDF report or share via WhatsApp. You can also download a printable checklist for offline use!',
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-rose-50',
    },
  ];

  useEffect(() => {
    // Check if user has seen onboarding
    const hasSeen = localStorage.getItem(ONBOARDING_KEY);
    if (!hasSeen) {
      // Small delay for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setIsVisible(false);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  if (!isVisible) return null;

  const step = steps[currentStep];
  const Icon = step.icon;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl animate-scale-in">
        {/* Header with Skip */}
        <div className="flex items-center justify-between px-5 pt-4">
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentStep 
                    ? 'w-6 bg-gradient-to-r ' + step.color 
                    : i < currentStep 
                      ? 'w-1.5 bg-slate-300' 
                      : 'w-1.5 bg-slate-200'
                }`}
              />
            ))}
          </div>
          <button
            onClick={handleSkip}
            className="text-slate-400 hover:text-slate-600 transition-colors text-sm font-medium flex items-center gap-1"
          >
            Skip
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 pt-4">
          {/* Icon */}
          <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg mb-5`}>
            <Icon className="w-10 h-10 text-white" />
          </div>

          {/* Text */}
          <h2 className="text-xl font-bold text-slate-800 text-center mb-2">
            {step.title}
          </h2>
          <p className="text-slate-600 text-center text-sm leading-relaxed">
            {step.description}
          </p>
        </div>

        {/* Navigation */}
        <div className="px-5 pb-5 flex gap-3">
          {currentStep > 0 ? (
            <button
              onClick={handlePrev}
              className="flex-1 py-3 px-4 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold flex items-center justify-center gap-1 hover:bg-slate-50 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          ) : (
            <div className="flex-1" />
          )}
          <button
            onClick={handleNext}
            className={`flex-1 py-3 px-4 rounded-xl text-white font-semibold flex items-center justify-center gap-1 bg-gradient-to-r ${step.color} shadow-lg hover:shadow-xl transition-all`}
          >
            {isLastStep ? (
              <>
                <Sparkles className="w-4 h-4" />
                Get Started
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>

        {/* Step indicator text */}
        <div className="px-5 pb-4 text-center">
          <span className="text-xs text-slate-400">
            {currentStep + 1} of {steps.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
